import { get, isNil } from 'lodash';
import PropTypes from 'prop-types';
import { isBlank } from '../../properties/funcs';
import { DEFAULT_INVALID_VALUE } from '../../../utilities/FormattingUtilities';
import { AdminPages } from '../AdminPages';
import { DrillInLink } from '../../links/DrillInLink';
import { buildRootPageTarget } from '../AdminLinkUtils';
import useNgpFormContext from '../../../utilities/NgpFormContextProviderUtilities';

/**
 * Creates a drill-in link for infrastructure entities in the admin interface
 * Returns invalid value placeholder if data is missing or blank
 *
 * @param {Object} config - Configuration object
 * @param {string} config.nameField - Field path for the display name
 * @param {string} config.idField - Field path for the entity ID/UUID
 * @returns {JSX.Element} DrillInLink component or default invalid value
 */
export const useInfrastructureLink = ({ nameField, idField }) => {
    const { formData } = useNgpFormContext();

    const name = get(formData, nameField);
    const id = get(formData, idField);

    const isValid = !isBlank(name) && !isNil(id);

    const url = isValid ? `${buildRootPageTarget(AdminPages.VirtualInfrastructure)}/${id}` : '';

    return {
        name,
        id,
        url,
        isValid,
    };
};

/**
 * Generic infrastructure link component
 * Displays a drill-in link to an infrastructure entity or invalid value placeholder
 *
 * @param {Object} props - Component props
 * @param {string} props.nameField - Field path for the display name
 * @param {string} props.idField - Field path for the entity ID/UUID
 * @returns {JSX.Element} Link component or invalid value placeholder
 */
export const InfrastructureLink = ({ nameField, idField }) => {
    const { name, url, isValid } = useInfrastructureLink({ nameField, idField });

    if (!isValid) {
        return <>{DEFAULT_INVALID_VALUE}</>;
    }

    return (
        <DrillInLink to={url} title={name}>
            {name}
        </DrillInLink>
    );
};

InfrastructureLink.propTypes = {
    nameField: PropTypes.string.isRequired,
    idField: PropTypes.string.isRequired,
};
